import { useEffect, useState, useRef } from 'react';
import { ArrowRight, X, Sparkles } from 'lucide-react';

interface OnboardingTooltipProps {
  targetSelector: string;
  title: string;
  description: string;
  step: number;
  totalSteps: number;
  position?: 'top' | 'bottom' | 'left' | 'right';
  onNext?: () => void;
  onSkip?: () => void;
  showNextButton?: boolean;
  nextLabel?: string;
  isVisible: boolean;
}

export function OnboardingTooltip({
  targetSelector,
  title,
  description,
  step,
  totalSteps,
  position = 'bottom',
  onNext,
  onSkip,
  showNextButton = true,
  nextLabel = 'Suivant',
  isVisible,
}: OnboardingTooltipProps) {
  const [coords, setCoords] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isVisible) {
      setCoords(null);
      return;
    }

    const updatePosition = () => {
      const target = document.querySelector(targetSelector);
      if (target) {
        const rect = target.getBoundingClientRect();
        setCoords({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          height: rect.height,
        });
      } else {
        // Target not found - hide overlay
        setCoords(null);
      }
    };

    // Initial position
    updatePosition();

    // Update on scroll/resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    // MutationObserver for dynamic content
    const observer = new MutationObserver(updatePosition);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      observer.disconnect();
    };
  }, [targetSelector, isVisible]);

  if (!isVisible || !coords) return null;

  // Calculate tooltip position
  const getTooltipStyle = () => {
    const padding = 12;
    const arrowSize = 8;

    switch (position) {
      case 'top':
        return {
          bottom: `calc(100vh - ${coords.top - padding}px)`,
          left: coords.left + coords.width / 2,
          transform: 'translateX(-50%)',
        };
      case 'bottom':
        return {
          top: coords.top + coords.height + padding + arrowSize,
          left: coords.left + coords.width / 2,
          transform: 'translateX(-50%)',
        };
      case 'left':
        return {
          top: coords.top + coords.height / 2,
          right: `calc(100vw - ${coords.left - padding}px)`,
          transform: 'translateY(-50%)',
        };
      case 'right':
        return {
          top: coords.top + coords.height / 2,
          left: coords.left + coords.width + padding + arrowSize,
          transform: 'translateY(-50%)',
        };
      default:
        return {};
    }
  };

  const getArrowStyle = () => {
    switch (position) {
      case 'top':
        return 'bottom-[-6px] left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-emerald-500';
      case 'bottom':
        return 'top-[-6px] left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-emerald-500';
      case 'left':
        return 'right-[-6px] top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-emerald-500';
      case 'right':
        return 'left-[-6px] top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-emerald-500';
      default:
        return '';
    }
  };

  return (
    <>
      {/* Highlight overlay - z-[200] to stay above modals */}
      <div className="fixed inset-0 z-[200] pointer-events-none">
        {/* Semi-transparent backdrop with cutout */}
        <div 
          className="absolute inset-0 bg-black/50 transition-all duration-300"
          style={{
            clipPath: `polygon(
              0% 0%, 
              0% 100%, 
              ${coords.left - 8}px 100%, 
              ${coords.left - 8}px ${coords.top - 8}px, 
              ${coords.left + coords.width + 8}px ${coords.top - 8}px, 
              ${coords.left + coords.width + 8}px ${coords.top + coords.height + 8}px, 
              ${coords.left - 8}px ${coords.top + coords.height + 8}px, 
              ${coords.left - 8}px 100%, 
              100% 100%, 
              100% 0%
            )`,
          }}
        />
        {/* Highlight border */}
        <div
          className="absolute border-2 border-emerald-400 rounded-xl shadow-[0_0_0_4px_rgba(16,185,129,0.3)] animate-pulse"
          style={{
            top: coords.top - 8,
            left: coords.left - 8,
            width: coords.width + 16,
            height: coords.height + 16,
          }}
        />
      </div>

      {/* Tooltip - z-[210] to stay above everything */}
      <div
        ref={tooltipRef}
        className="fixed z-[210] w-72 pointer-events-auto animate-in fade-in zoom-in-95 duration-200"
        style={getTooltipStyle()}
      >
        {/* Arrow */}
        <div className={`absolute w-0 h-0 border-[6px] ${getArrowStyle()}`} />

        {/* Content */}
        <div className="bg-zinc-900 border border-emerald-500/50 rounded-xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 bg-emerald-500/20 border-b border-emerald-500/30">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <span className="text-xs font-medium text-emerald-400">
                Étape {step}/{totalSteps}
              </span>
            </div>
            {onSkip && (
              <button
                onClick={onSkip}
                className="p-1 text-zinc-400 hover:text-white transition-colors"
                aria-label="Passer"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Body */}
          <div className="px-4 py-3">
            <h4 className="font-semibold text-white text-sm mb-1">{title}</h4>
            <p className="text-xs text-zinc-300 leading-relaxed">{description}</p>
          </div>

          {/* Footer */}
          {showNextButton && onNext && (
            <div className="px-4 py-2 bg-zinc-800/50 border-t border-zinc-700/50">
              <button
                onClick={onNext}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
              >
                {nextLabel}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default OnboardingTooltip;
